import { NavLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useMsal } from '@azure/msal-react'
import { useRole } from '../../hooks/useMe'
import { useFleet } from '../../hooks/useFleet'
import { getPrioritizedFindings } from '../../services/api'
import { Sym } from '../ui/Sym'
import AlphaBoldMark from '../ui/AlphaBoldMark'
import WorkspaceSwitcher from './WorkspaceSwitcher'
import { useScopeStore } from '../../store/scopeStore'
import type { RoleKey } from '../../types'

interface NavItem {
  label: string
  path: string
  icon: string
  roles?: RoleKey[]
  /** Plan feature required to see this item (internal users are unlimited). */
  feature?: string
  badge?: 'risky' | 'findings'
  /** Hidden from customer-portal users (MSP-only surface). */
  internalOnly?: boolean
}
interface NavGroup {
  group: string
  items: NavItem[]
  /** Whole group hidden from customer-portal users. */
  internalOnly?: boolean
}

const NAV: NavGroup[] = [
  { group: 'OVERVIEW', items: [{ label: 'Dashboard', path: '/dashboard', icon: 'space_dashboard' }] },
  { group: 'IDENTITY', items: [
    { label: 'MFA & Auth Methods', path: '/mfa', icon: 'fingerprint' },
    { label: 'Risky Users', path: '/risky-users', icon: 'person_alert', badge: 'risky' },
    // No `feature` and no `internalOnly`: Identity is baseline and the backend router
    // carries no requireFeature. Placed before Privileged Access because gated items
    // trail ungated ones in this group.
    { label: 'Conditional Access', path: '/conditional-access', icon: 'policy' },
    // Ungated for the same reason; also before the gated Privileged Access entry.
    { label: 'App Credentials', path: '/app-credentials', icon: 'key' },
    // Privileged Access: Entra PIM is customer-visible when the org has the 'privileged'
    // module; the MSP cross-platform register tab is hidden for customers in the page.
    { label: 'Privileged Access', path: '/privileged', icon: 'admin_panel_settings', feature: 'privileged' },
  ] },
  // Devices is its OWN domain group, not a child of THREATS, for three reasons:
  //   - it has its own `devices` plan feature (deliberately not `monitor`), so
  //     filing it under threat monitoring contradicts how it is sold;
  //   - most of the page is inventory, compliance, encryption and lifecycle —
  //     exposure and CVEs are the minority of it;
  //   - for a tenant with no Intune and no Defender the hub shows pure Entra
  //     lifecycle data with no security content at all, so "Threats" would
  //     mislabel it exactly where the honest-degradation work matters most.
  // Sits ABOVE Threats: inventory precedes findings about it, mirroring how
  // IDENTITY sits above the surfaces that analyse identity.
  { group: 'DEVICES', items: [
    { label: 'Devices', path: '/devices', icon: 'devices', feature: 'devices' },
  ] },
  { group: 'THREATS', items: [
    { label: 'Threats', path: '/threats', icon: 'security' },
  ] },
  // Compliance is its OWN group rather than an item under POSTURE, mirroring how DEVICES
  // was separated: it has its own `compliance` plan feature, and most of it is Purview
  // configuration inventory rather than a posture score.
  //
  // `feature` here only HIDES the nav entry. The gate that enforces anything is
  // requireFeature('compliance') on the backend mount; a customer with a crafted request
  // reaches the API regardless of what this sidebar renders.
  { group: 'COMPLIANCE', items: [
    { label: 'Compliance & DLP', path: '/compliance', icon: 'privacy_tip', feature: 'compliance' },
  ] },
  { group: 'POSTURE', items: [
    { label: 'Secure Score', path: '/secure-score', icon: 'shield' },
    { label: 'Assessments', path: '/assessments', icon: 'assignment', badge: 'findings' },
    { label: 'Discover', path: '/discover', icon: 'travel_explore', feature: 'discover' },
    // Licensing is customer-visible when the org has the 'licensing' module.
    { label: 'Licensing', path: '/licensing', icon: 'sell', feature: 'licensing' },
    // Fleet-wide IOC watchlist is an MSP surface — internal only.
    { label: 'Threat Indicators', path: '/threat-indicators', icon: 'crisis_alert', internalOnly: true },
  ] },
  { group: 'CHANGE MONITOR', items: [
    { label: 'Overview', path: '/changes', icon: 'manage_history', feature: 'change_monitor' },
    { label: 'Change Log', path: '/changes/log', icon: 'format_list_bulleted', feature: 'change_monitor' },
  ] },
  // MSP operational surfaces — hidden entirely from the customer portal.
  { group: 'TENANTS', internalOnly: true, items: [
    { label: 'Tenants', path: '/tenants', icon: 'apartment' },
    { label: 'Onboard Tenant', path: '/onboarding', icon: 'add_business', roles: ['SuperAdmin', 'Analyst'] },
    { label: 'Organizations', path: '/organizations', icon: 'corporate_fare', roles: ['SuperAdmin', 'Analyst', 'Auditor'] },
    { label: 'Plans & Seats', path: '/entitlements', icon: 'receipt_long', roles: ['SuperAdmin', 'Analyst', 'Auditor'] },
    // Available to every AlphaBOLD (internal) user, whatever their role; the group is already
    // internal-only. Covers every module, so it is not gated on the change_monitor feature.
    { label: 'Alert settings', path: '/changes/alerts', icon: 'notifications' },
  ] },
  // Customer Global Admins only (derived role; internal users never hold it). Placed
  // after TENANTS on purpose: it is access administration, not an alertable module.
  { group: 'ADMINISTRATION', items: [
    { label: 'Access management', path: '/access', icon: 'manage_accounts', roles: ['CustomerGlobalAdmin'] },
  ] },
]

interface SidebarProps {
  /** Whether the mobile drawer is open (ignored on lg+ where the sidebar is static). */
  mobileOpen?: boolean
  /** Called to close the mobile drawer (e.g. after navigating). */
  onClose?: () => void
}

export default function Sidebar({ mobileOpen = false, onClose }: SidebarProps) {
  const { me, hasRole } = useRole()
  const { accounts, instance } = useMsal()
  const { kpis, rows } = useFleet()
  const scope = useScopeStore((s) => s.scope)
  const findings = useQuery({ queryKey: ['findings-prioritized'], queryFn: getPrioritizedFindings })

  const ent = me?.entitlements
  const hasFeature = (f: string): boolean => !!ent && (ent.unlimited || ent.features?.[f] === true)

  // Customer-portal users get a branded, scoped shell: MSP-only surfaces hidden,
  // the org's brand in place of AlphaBOLD. Internal users are unaffected.
  const isCustomer = me?.userType === 'customer'
  // MSP-only entries need a POSITIVE internal identification. Gating on `!isCustomer`
  // failed open: while /api/auth/me was loading, or when it errored, `me` was undefined
  // and a customer saw Tenants, Organizations, Threat Indicators and Alert settings.
  const isInternal = me?.userType === 'internal'
  const branding = me?.branding

  // Badges follow the workspace scope: portfolio-wide under 'fleet', otherwise
  // the SELECTED tenant's own counts (mirrors the Risky Users / Assessments pages).
  const isFleet = scope === 'fleet'
  const allFindings = findings.data ?? []
  const badges: Record<string, number> = {
    risky: isFleet ? kpis.totalRisky : rows.find((r) => r.id === scope)?.risky ?? 0,
    findings: isFleet
      ? allFindings.length
      : allFindings.filter((f) => f.tenants.some((t) => t.tenantId === scope)).length,
  }

  const user = accounts[0]
  const displayName = user?.name ?? user?.username ?? 'User'
  const email = user?.username ?? ''
  const initials = displayName.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()

  return (
    <aside
      className={
        'fixed inset-y-0 left-0 z-50 w-[252px] bg-sidebar border-r border-border-soft flex flex-col py-4 ' +
        'transform transition-transform duration-200 ease-out overflow-visible flex-shrink-0 ' +
        'lg:static lg:z-auto lg:translate-x-0 ' +
        (mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full')
      }
    >
      {/* Logo / brand — org branding for customer-portal users, AlphaBOLD otherwise */}
      <div className="flex items-center gap-2.5 px-[18px] pb-4">
        {isCustomer ? (
          <>
            {branding?.logoUrl
              ? <img src={branding.logoUrl} alt="" className="h-[34px] w-[34px] rounded object-contain flex-shrink-0" />
              : <AlphaBoldMark size={34} />}
            <div className="leading-tight min-w-0">
              <div className="font-display text-[15px] leading-none tracking-tight text-text-primary truncate">{branding?.productName || 'Security Portal'}</div>
              <div className="text-[11px] text-text-faint mt-1">Client portal</div>
            </div>
          </>
        ) : (
          <>
            <AlphaBoldMark size={34} />
            <div className="leading-tight">
              <div className="font-display text-[15px] leading-none tracking-tight">
                <span className="font-normal text-text-primary">alpha</span><span className="font-extrabold text-primary">bold</span>
              </div>
              <div className="text-[11px] text-text-faint mt-1">SecOps Platform</div>
            </div>
          </>
        )}
      </div>

      <WorkspaceSwitcher />

      <nav className="flex-1 overflow-y-auto px-3">
        {NAV.map((grp) => {
          if (grp.internalOnly && !isInternal) return null
          const items = grp.items.filter((it) =>
            (!it.roles || hasRole(...it.roles)) &&
            (!it.feature || hasFeature(it.feature)) &&
            (!it.internalOnly || isInternal)
          )
          if (items.length === 0) return null
          return (
            <div key={grp.group} className="mb-3">
              <div className="font-mono text-[10px] tracking-[1.5px] text-text-faint px-2.5 pb-1.5">{grp.group}</div>
              {items.map((it) => {
                const badge = it.badge ? badges[it.badge] : 0
                return (
                  <NavLink
                    key={it.path}
                    to={it.path}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `w-full flex items-center gap-2.5 px-2.5 py-2 mb-0.5 rounded-[9px] text-[13.5px] transition-colors ` +
                      (isActive ? 'bg-primary/10 text-text-primary font-semibold' : 'text-text-muted font-normal hover:bg-surface-raised hover:text-text-primary')
                    }
                  >
                    <Sym name={it.icon} size={19} />
                    <span className="flex-1 text-left">{it.label}</span>
                    {badge > 0 && (
                      <span className="font-mono text-[10px] font-semibold bg-surface-raised text-text-muted border border-border px-1.5 rounded-[10px]">{badge}</span>
                    )}
                  </NavLink>
                )
              })}
            </div>
          )
        })}
      </nav>

      {/* User footer */}
      <div className="border-t border-border-soft mx-3 mt-2 pt-3.5 px-1.5 flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full bg-primary-700 flex items-center justify-center text-xs font-semibold">{initials}</div>
        <div className="flex-1 leading-tight min-w-0">
          <div className="text-[13px] font-semibold truncate">{displayName}</div>
          <div className="text-[11px] text-text-faint font-mono truncate">{email}</div>
        </div>
        <button onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })} aria-label="Sign out">
          <Sym name="logout" size={18} className="text-text-faint hover:text-text-primary" />
        </button>
      </div>
    </aside>
  )
}
